// src/lib/contrat-html.js — Phase 2.E.9 (ADR-042) · CONTRAT V2 (2026-08)
//   + 06/05/2026 architecture multi-émetteur (Bug #1 audit visuel global) :
//   sous-loc (prop.id === 'p6') = émetteur FBZ SAS, sinon = émetteur proprio.
// Générateur HTML imprimable d'un contrat de location courte durée.
//
// ⚠️ MIROIR ESM de lib/contrat-html.js (CJS serveur — le CANONIQUE, voir son
// cartouche pour la doc complète). Le CORPS de ce fichier (tout sauf ce
// cartouche, les imports et les exports) est une COPIE EXACTE du CJS. NE PAS
// éditer l'un sans l'autre (duplication contrôlée ADR-021 — CRA interdit
// l'import racine lib/ depuis src/). Parité prouvée par
// src/lib/contrat-html.parity.test.js (HTML strictement byte-identique).

import { resolveEmitter } from './emitter-resolver'
// LOT A (moteur contrat templatable) — variables {var} + drapeaux {#if flag}
// des clauses. MIROIR CJS ↔ ESM également (src/lib/contrat-vars.js).
import { buildContratVars, renderContratClauses, formatSignatureDateTimeFr } from './contrat-vars'
// Nom du logement : règle UNIQUE partagée avec les factures (30/09) — le nom
// propre de la chambre, jamais celui de la maison, jamais l'identifiant.
import { nomLogementFacture } from './nom-logement-facture'
// Montant de caution : règle UNIQUE partagée avec la préautorisation Stripe
// (décision Dimitri 07/10 : 0 = pas de caution, vide = celle du logement parent).
// P-2.56 (30/10) : LA porte de la caution d'un séjour (figée sinon calculée pour la durée).
import { cautionPourSejour, nuitsEntre } from './caution-figee'

// CONTRAT V2 (2026-08) — texte par défaut des clauses : les 17 articles du
// contrat de location saisonnière v2 (docs/handoff/contrat-location-saisonniere-v2.md),
// VERBATIM (marqueurs markdown retirés), TEMPLATABLES ({var} + {#if flag} du
// catalogue lib/contrat-vars.js). Éditable par l'admin via
// cfg.props[].logs[].contrat_clauses (la validation api/cfg refuse tout token
// inconnu / {#if} malformé — un texte vide = ce défaut).
//
// Conventions de MISE EN FORME comprises par le pipeline de rendu (renderClausesHtml) :
//   - « Article N — Titre » en 1re ligne de paragraphe   → titre d'article ;
//   - paragraphe d'une seule ligne courte « N.M … »      → sous-titre (4.1 Montant…) ;
//   - lignes « | Libellé | Valeur »                      → table épurée (tableaux du doc) ;
//   - lignes « - … »                                     → liste à puces.
//
// CAUTION (art. 4.3 / 5.2 / 6 / 7.4 / 8.3) : le texte dit EXACTEMENT ce que fait
// le moteur (matrice caution via {#if caution_due}/{#if caution_absente} ;
// mode Stripe via {#if caution_empreinte}/{#if caution_debit}, seuil servi par
// {seuil_nuits_caution} — jamais un chiffre en dur). L'article 6 existe en
// TROIS variantes complètes mutuellement exclusives (absente / empreinte / débit).
//
// MÉDIATEUR (art. 16) : rendu UNIQUEMENT si émetteur professionnel (FBZ SAS,
// {#if loueur_professionnel}) ET médiateur configuré (cfg.co.mediateur_* ou env
// MEDIATEUR_NOM/MEDIATEUR_COORDONNEES) — jamais de médiateur inventé.
export const DEFAULT_CLAUSES = `Soumis aux articles 1708 et suivants du Code civil, à la loi n° 70-9 du 2 janvier 1970 et à l'article L. 324-2 du Code du tourisme. Le présent contrat n'est pas régi par la loi n° 89-462 du 6 juillet 1989.

Article 1 — Objet

Le Loueur donne en location au Voyageur, qui accepte, le logement meublé désigné à l'article 2, à usage exclusif de villégiature et pour une occupation temporaire.

Le Voyageur déclare et reconnaît expressément que le logement ne constituera en aucun cas sa résidence principale, ni celle des personnes l'accompagnant, et qu'il n'y exercera aucune activité professionnelle ni commerciale.

Cette destination est une condition déterminante du consentement du Loueur : sans elle, le contrat n'aurait pas été conclu.

Article 2 — Désignation du logement

| Logement | {logement_nom}
| Adresse | {logement_adresse}
| Composition | {logement_composition}
| Capacité maximale | {logement_capacite} personnes

Le Voyageur reconnaît avoir pris connaissance, avant la conclusion du présent contrat, du descriptif du logement, de ses équipements et de ses photographies, tels que présentés lors de la réservation.

Article 3 — Durée

La location est consentie du {date_arrivee} à partir de {heure_arrivee} au {date_depart} avant {heure_depart}, soit {nombre_nuits} nuits.

Cette durée est ferme et non renouvelable par tacite reconduction. Elle ne peut en aucun cas excéder quatre-vingt-dix (90) jours consécutifs.

Le Voyageur s'engage à avoir intégralement libéré le logement à l'heure de départ convenue. Tout maintien dans les lieux au-delà de cette heure, sans accord préalable du Loueur, donnera lieu à une indemnité d'occupation forfaitaire égale à une nuitée, sans préjudice des dommages causés au Loueur, notamment l'impossibilité d'accueillir le voyageur suivant.

Article 4 — Prix et modalités de paiement

4.1 Montant

| Hébergement | {montant_hebergement} €
| Frais de ménage | {montant_menage} €
| Taxe de séjour | {montant_taxe} €
| Montant total du séjour | {montant_total} €

La taxe de séjour est collectée pour le compte de la commune et intégralement reversée à celle-ci. Son montant est calculé selon le tarif en vigueur, le nombre de personnes assujetties et le nombre de nuitées.

4.2 Réservation ferme

Les sommes versées au titre du présent contrat constituent un acompte et non des arrhes. En conséquence, et par dérogation à l'article 1590 du Code civil, aucune des parties ne peut se dédire : le Voyageur reste tenu du paiement intégral du séjour, et le Loueur de la mise à disposition du logement.

4.3 Blocage des dates

Les dates sont bloquées au moment où le Voyageur engage le paiement du séjour, et définitivement réservées à l'encaissement effectif du paiement intégral.

La signature du présent contrat ne suffit pas à bloquer les dates. Jusqu'à l'engagement du paiement, le logement demeure disponible à la réservation par tout autre voyageur, et le Loueur ne saurait voir sa responsabilité engagée si les dates venaient à être réservées entre-temps. Si le paiement engagé n'aboutit pas, le blocage est levé et le logement redevient disponible.

À défaut de paiement dans un délai de quarante-huit (48) heures à compter de la signature du contrat, la réservation est réputée caduque de plein droit, sans mise en demeure préalable.

Le paiement du séjour ne dispense pas le Voyageur des formalités de l'article 5, dont l'accomplissement conditionne la remise des moyens d'accès au logement. La réservation n'est définitivement acquise qu'une fois le paiement encaissé{#if caution_due}, le formulaire de bienvenue complété et le dépôt de garantie constitué{/if}{#if caution_absente} et le formulaire de bienvenue complété{/if}.

4.4 Absence de droit de rétractation

Conformément à l'article L. 221-28, 12° du Code de la consommation, les prestations de services d'hébergement fournies à une date déterminée ne relèvent pas du droit de rétractation. Le Voyageur ne dispose donc d'aucun délai de rétractation, quelle que soit la modalité de conclusion du contrat.

Article 5 — Formalités préalables obligatoires

Le Voyageur s'engage à accomplir, avant son arrivée, les formalités suivantes :

5.1 — Compléter le formulaire de bienvenue transmis par le Loueur, incluant l'identité de l'ensemble des occupants, les horaires d'arrivée et de départ prévus, ainsi que la transmission d'une pièce d'identité en cours de validité du signataire du contrat.

{#if caution_due}5.2 — Constituer le dépôt de garantie dans les conditions de l'article 6.

{/if}Ces formalités constituent des conditions suspensives de la remise des moyens d'accès au logement. À défaut d'accomplissement, le Loueur est fondé à refuser l'accès au logement, sans que le Voyageur puisse prétendre à un quelconque remboursement.

La collecte de la pièce d'identité répond à une double nécessité : la vérification de l'identité de l'occupant du logement, et le respect du décret n° 2015-1002 du 18 août 2015 imposant l'établissement d'une fiche individuelle de police pour les voyageurs de nationalité étrangère.

{#if caution_absente}Article 6 — Dépôt de garantie

Aucun dépôt de garantie n'est demandé pour le présent séjour. Le Voyageur demeure néanmoins responsable, dans les conditions du droit commun et de l'article 10, des dommages causés au logement, à son mobilier ou à ses équipements.{/if}{#if caution_empreinte}Article 6 — Dépôt de garantie

6.1 Constitution

Un dépôt de garantie de {montant_caution} € est constitué sous forme d'empreinte bancaire (pré-autorisation sur carte bancaire), à compter de {jours_avant_caution} jours avant la date d'arrivée.

Aucune somme n'est prélevée sur le compte du Voyageur au titre de cette empreinte. Le montant est simplement réservé auprès de l'établissement bancaire.{/if}{#if caution_empreinte ET nuits_connues} La durée du séjour n'excédant pas {seuil_nuits_caution} nuits, l'empreinte couvre l'intégralité du séjour.{/if}{#if caution_empreinte}

6.2 Objet

Le dépôt de garantie a pour objet de couvrir :
- les dommages, dégradations ou pertes affectant le logement, son mobilier, ses équipements ou ses accessoires ;
- la perte ou la non-restitution des moyens d'accès ;
- les frais de ménage supplémentaires rendus nécessaires par un état de restitution anormal ;
- l'indemnité d'occupation prévue à l'article 3 en cas de départ tardif non autorisé ;
- toute somme restant due au titre du présent contrat, notamment les services additionnels commandés.

6.3 Restitution

Le dépôt de garantie est libéré dans un délai maximal de sept (7) jours suivant le départ, sous réserve de l'absence de dommage constaté.

En cas de retenue, le Loueur en informe le Voyageur par écrit, en précisant le motif et le montant retenu. Le Voyageur peut exiger la production des justificatifs correspondants (devis, facture, constat, photographies).

Le solde éventuel est libéré dans le même délai. La libération effective des fonds sur le compte du Voyageur dépend des délais de traitement de son établissement bancaire, généralement de cinq à dix jours ouvrés.

6.4 Insuffisance

Si le montant des dommages excède le dépôt de garantie, le Voyageur demeure redevable de la différence.{/if}{#if caution_debit}Article 6 — Dépôt de garantie

6.1 Constitution

Un dépôt de garantie de {montant_caution} € est constitué par prélèvement sur la carte bancaire du Voyageur, à compter de {jours_avant_caution} jours avant la date d'arrivée.

La durée du séjour excédant {seuil_nuits_caution} nuits, le dépôt ne peut être constitué par simple empreinte bancaire : le montant est effectivement débité du compte du Voyageur, puis intégralement remboursé après le départ dans les conditions du présent article.

6.2 Objet

Le dépôt de garantie a pour objet de couvrir :
- les dommages, dégradations ou pertes affectant le logement, son mobilier, ses équipements ou ses accessoires ;
- la perte ou la non-restitution des moyens d'accès ;
- les frais de ménage supplémentaires rendus nécessaires par un état de restitution anormal ;
- l'indemnité d'occupation prévue à l'article 3 en cas de départ tardif non autorisé ;
- toute somme restant due au titre du présent contrat, notamment les services additionnels commandés.

6.3 Restitution

Le dépôt de garantie est remboursé dans un délai maximal de sept (7) jours suivant le départ, sous réserve de l'absence de dommage constaté.

En cas de retenue, le Loueur en informe le Voyageur par écrit, en précisant le motif et le montant retenu. Le Voyageur peut exiger la production des justificatifs correspondants (devis, facture, constat, photographies).

Le solde éventuel est remboursé dans le même délai. Le crédit effectif des fonds sur le compte du Voyageur dépend des délais de traitement de son établissement bancaire, généralement de cinq à dix jours ouvrés.

6.4 Insuffisance

Si le montant des dommages excède le dépôt de garantie, le Voyageur demeure redevable de la différence.{/if}

Article 7 — Arrivée, départ et remise des accès

7.1 — Les moyens d'accès (clés, codes, boîte à clés) sont transmis au Voyageur après accomplissement des formalités de l'article 5, la veille de l'arrivée.

7.2 — Constat de l'état du logement

L'état du logement est constaté par le Loueur au moyen de photographies horodatées réalisées après chaque départ, dans le cadre de la remise en état. Les parties conviennent expressément que ces photographies font foi entre elles de l'état du logement à la date de leur prise.

Le Voyageur s'engage à signaler au Loueur, dans les vingt-quatre (24) heures suivant son arrivée, toute anomalie, dégradation préexistante ou absence d'équipement, si possible accompagnée de photographies.

À défaut de signalement dans ce délai, le logement est réputé avoir été reçu conforme au constat photographique établi lors du départ précédent. Le Voyageur ne pourra alors utilement contester être à l'origine des dégradations constatées à l'issue de son séjour.

7.3 — Le Voyageur restitue le logement dans un état de propreté normal : vaisselle propre et rangée, déchets évacués, mobilier remis en place.

7.4 — Le Voyageur remet les moyens d'accès selon les modalités convenues. {#if caution_due}Toute perte ou non-restitution donne lieu à retenue sur le dépôt de garantie du coût de remplacement, y compris le remplacement du cylindre de serrure si la sécurité du logement l'exige.{/if}{#if caution_absente}Toute perte ou non-restitution donne lieu à facturation au Voyageur du coût de remplacement, y compris le remplacement du cylindre de serrure si la sécurité du logement l'exige.{/if}

Article 8 — Obligations du Voyageur

8.1 Occupation

Le Voyageur occupe le logement personnellement et s'engage à en user raisonnablement, dans le respect de sa destination et du voisinage.

Le nombre d'occupants ne peut excéder {logement_capacite} personnes, enfants et nourrissons compris. Tout dépassement constitue un manquement grave autorisant le Loueur à résilier le contrat sans préavis ni indemnité, dans les conditions de l'article 13.

8.2 Interdictions

Sont formellement interdits :
- la sous-location, la cession du contrat, ou la mise à disposition du logement à des tiers, à titre gratuit ou onéreux ;
- l'organisation de fêtes, soirées, réceptions ou tout rassemblement excédant le nombre d'occupants autorisé ;
- toute activité commerciale, professionnelle, ou tournage sans autorisation écrite préalable ;
- toute nuisance sonore de nature à troubler la tranquillité du voisinage, en particulier entre 22 h et 8 h ;
- toute modification de l'agencement du logement ou de ses installations.

8.3 Règles propres au logement

{#if animaux_non}Les animaux ne sont pas admis dans le logement.{/if}
{#if fumeur_non}Il est formellement interdit de fumer ou de vapoter à l'intérieur du logement.{/if}{#if fumeur_non ET caution_due} Tout manquement donne lieu à une retenue sur le dépôt de garantie au titre des frais de désodorisation et de nettoyage approfondi.{/if}{#if fumeur_non ET caution_absente} Tout manquement donne lieu à facturation au Voyageur des frais de désodorisation et de nettoyage approfondi.{/if}
Le Voyageur s'engage à respecter le règlement intérieur de l'immeuble et les règles de vie collective portées à sa connaissance.

8.4 Diligence

Le Voyageur signale sans délai au Loueur toute panne, fuite, sinistre ou dysfonctionnement, afin de permettre une intervention rapide. L'aggravation d'un dommage résultant d'un défaut de signalement engage sa responsabilité.

Article 9 — Accès internet

Le Voyageur bénéficiant d'un accès à internet s'engage à ne pas l'utiliser pour télécharger, partager ou diffuser des œuvres protégées par un droit d'auteur sans autorisation.

Il est rappelé que le titulaire de l'accès est tenu de le sécuriser, sous peine d'engager sa responsabilité (article L. 336-3 du Code de la propriété intellectuelle). Cette responsabilité n'exclut pas celle du Voyageur, qui peut se voir reprocher un délit de contrefaçon (article L. 335-3 du même code).

Le Voyageur garantit le Loueur contre toute réclamation ou poursuite résultant de son usage de l'accès internet.

Article 10 — Assurance et responsabilité

10.1 — Il est vivement recommandé au Voyageur de vérifier que son assurance responsabilité civile couvre les dommages qu'il pourrait causer dans un logement de villégiature.

10.2 — Le Loueur ne peut être tenu responsable des vols, pertes ou dégradations affectant les effets personnels du Voyageur, ni des dommages résultant du fait d'un tiers.

10.3 — Le Voyageur est responsable de l'ensemble des dommages causés par lui-même, les personnes l'accompagnant, ou toute personne qu'il aurait introduite dans le logement.

10.4 — Le Loueur ne saurait être tenu responsable des interruptions temporaires de services extérieurs à sa volonté (électricité, eau, internet, chauffage collectif), sauf carence caractérisée de sa part.

Article 11 — Annulation

11.1 Annulation par le Voyageur

{clause_annulation}

La taxe de séjour, collectée pour le compte de la commune, est intégralement remboursée en cas d'annulation, quelle que soit la politique applicable.

Tout remboursement dû est effectué sur le moyen de paiement utilisé lors de la réservation, dans un délai de sept (7) jours à compter de l'annulation.

Le Voyageur est invité à souscrire une assurance annulation auprès de l'organisme de son choix.

11.2 Annulation par le Loueur

En cas d'annulation du fait du Loueur, hors force majeure, l'intégralité des sommes versées est restituée au Voyageur dans un délai de sept (7) jours, sans autre indemnité.

Le Loueur s'efforcera, dans la mesure du possible, de proposer une solution de relogement équivalente.

Article 12 — Force majeure

En cas de survenance d'un événement de force majeure au sens de l'article 1218 du Code civil rendant impossible l'exécution du contrat, celui-ci est résilié de plein droit.

Les sommes versées sont restituées au Voyageur au prorata de la période non exécutée, sans indemnité de part et d'autre.

Article 13 — Résiliation pour manquement

En cas de manquement grave du Voyageur à ses obligations — notamment dépassement de la capacité d'accueil, organisation d'une fête, nuisances répétées, sous-location, ou dégradation volontaire — le Loueur pourra résilier le contrat de plein droit et exiger la libération immédiate du logement.

Le Loueur adresse au Voyageur une mise en demeure par tout moyen, y compris verbal ou téléphonique, confirmée ensuite par écrit. À défaut de cessation du manquement dans un délai de deux (2) heures, la résiliation est acquise.

Aucun remboursement ne sera dû, sans préjudice des dommages et intérêts que le Loueur serait fondé à réclamer.

Article 14 — Services additionnels

Le Voyageur peut commander des prestations complémentaires (arrivée anticipée, départ tardif, équipements, ménage supplémentaire) depuis son espace séjour.

Ces prestations sont soumises à acceptation préalable du Loueur et facturées séparément. Une commande n'est confirmée qu'après validation et paiement.

Le montant effectivement prélevé correspond à la prestation réellement accordée, qui ne peut excéder celle demandée.

Article 15 — Données personnelles

Les données collectées (identité, coordonnées, pièce d'identité, informations de séjour) sont traitées aux fins d'exécution du présent contrat, de vérification d'identité, et de respect des obligations légales.

Base légale : exécution du contrat (article 6.1.b du RGPD) et obligation légale s'agissant de la fiche individuelle de police (article 6.1.c).

Durée de conservation : la pièce d'identité est supprimée automatiquement quinze (15) jours après la fin du séjour. Les données de réservation sont conservées pendant la durée légale de prescription commerciale.

Droits : le Voyageur dispose d'un droit d'accès, de rectification, d'effacement, de limitation et d'opposition, qu'il peut exercer auprès du Loueur. Il dispose également du droit d'introduire une réclamation auprès de la Commission nationale de l'informatique et des libertés.

Aucune donnée n'est cédée à des tiers à des fins commerciales.

Article 16 — Réclamation

Toute réclamation doit être adressée au Loueur dans les meilleurs délais et si possible pendant le séjour, afin de permettre une résolution immédiate.

{#if loueur_professionnel ET mediateur_nom ET mediateur_coordonnees}Conformément aux articles L. 611-1 et suivants du Code de la consommation, le Voyageur peut recourir gratuitement à un médiateur de la consommation en cas de litige non résolu dans un délai de deux mois suivant sa réclamation écrite. Médiateur compétent : {mediateur_nom} — {mediateur_coordonnees}{/if}

Article 17 — Dispositions diverses

17.1 Intégralité — Le présent contrat et le livret d'accueil constituent l'intégralité de l'accord entre les parties.

17.2 Divisibilité — La nullité d'une clause n'entraîne pas celle du contrat. Les parties s'engagent à lui substituer une clause valable d'effet équivalent.

17.3 Signature électronique — Les parties reconnaissent la validité de la signature électronique du présent contrat conformément aux articles 1366 et 1367 du Code civil.

17.4 Loi applicable et juridiction — Le présent contrat est soumis au droit français. À défaut de résolution amiable, les tribunaux compétents seront ceux du lieu de situation du logement, sous réserve des règles protectrices applicables aux consommateurs.`;

// ── CONTRAT V2 — pipeline texte → HTML des clauses ───────────────────────────
//
// Appliqué APRÈS renderContratClauses ({var}/{#if} résolus sur le TEXTE). Par
// paragraphe (split \n\n) :
//   - 1re ligne « Article N — … »                → titre d'article (.art-title,
//     12px #3d5166, jamais en majuscules) ;
//   - paragraphe d'UNE ligne courte « N.M … »    → sous-titre (.art-sub) ;
//   - lignes « | Libellé | Valeur »              → table épurée (.clause-table) ;
//   - lignes « - … »                             → liste à puces (.clause-list) ;
//   - le reste                                   → texte (.clause-p, sauts
//     simples = <br>).
// Chaque ligne/cellule passe par escapeHtml (XSS-safe, valeurs injectées
// comprises). Les lignes vides résiduelles (blocs {#if} masqués) et les
// paragraphes vides sont ignorés — jamais de <p> fantôme dans un PDF signé.
const ARTICLE_TITLE_RE = /^Article \d{1,2} — .+$/;
const SUB_HEADING_RE = /^\d{1,2}\.\d{1,2}(?: —)? .{1,40}$/;

function renderClausesHtml(text) {
  const out = [];
  for (const rawPara of String(text == null ? '' : text).split('\n\n')) {
    const lines = rawPara.split('\n').filter((l) => l.trim() !== '');
    if (lines.length === 0) continue;
    let html = '';
    let body = lines;
    if (ARTICLE_TITLE_RE.test(lines[0].trim())) {
      html += `<div class="art-title">${escapeHtml(lines[0].trim())}</div>`;
      body = lines.slice(1);
    } else if (lines.length === 1 && SUB_HEADING_RE.test(lines[0].trim())) {
      out.push(`<div class="art-sub">${escapeHtml(lines[0].trim())}</div>`);
      continue;
    }
    let i = 0;
    while (i < body.length) {
      if (body[i].startsWith('| ')) {
        const rows = [];
        while (i < body.length && body[i].startsWith('| ')) {
          const cells = body[i].replace(/^\|\s*/, '').replace(/\s*\|\s*$/, '').split('|').map((c) => c.trim());
          rows.push(`<tr><td class="ct-label">${escapeHtml(cells[0] || '')}</td><td class="ct-value">${escapeHtml(cells.slice(1).join(' · '))}</td></tr>`);
          i += 1;
        }
        html += `<table class="clause-table"><tbody>${rows.join('')}</tbody></table>`;
      } else if (body[i].startsWith('- ')) {
        const items = [];
        while (i < body.length && body[i].startsWith('- ')) {
          items.push(`<li>${escapeHtml(body[i].slice(2))}</li>`);
          i += 1;
        }
        html += `<ul class="clause-list">${items.join('')}</ul>`;
      } else {
        const txt = [];
        while (i < body.length && !body[i].startsWith('| ') && !body[i].startsWith('- ')) {
          txt.push(escapeHtml(body[i]));
          i += 1;
        }
        html += `<p class="clause-p">${txt.join('<br>')}</p>`;
      }
    }
    if (html) out.push(html);
  }
  return out.join('');
}

// Génère le HTML complet d'un contrat. Mode 'preview' = inclut le bouton flottant Imprimer.
// Mode 'sign' = rendu pour signature voyageur (PAS de bouton print inline onclick).
// Si reservation null : on génère un contrat "type" (variables génériques) — utile pour
// envoi à un voyageur qui n'a pas encore réservé via plateforme directe.
//
// Contrat = FR STRICT (décision 2026-06-16 (b)) : les clauses sont TOUJOURS en FR
// (log.contrat_clauses || DEFAULT_CLAUSES), quelle que soit la locale voyageur. Le
// document signé n'a qu'une version juridiquement engageante (FR).
//
// CONTRAT V2 (2026-08) — design épuré validé (maquette ref-contrat + spec Dimitri) :
// en-tête centré (eyebrow / nom du logement / villégiature), filet ciel #87b8e6,
// parties LOUEUR/VOYAGEUR en 2 colonnes (voyageur SANS adresse), carte LOGEMENT
// #f7fafc (arrivée/départ + montant/caution), articles 12px #3d5166 (pas de
// majuscules), signatures sous trait #3d5166 avec zone de signature 44px, pied
// « Contrat émis le {date} par {émetteur} » — SANS AUCUNE mention FULLBNBZEN ni
// contact@ (l'émetteur FBZ SAS garde ses mentions SIRET dans le bloc LOUEUR).
// Charte 2026 bleu-gris (ADR-302) : #3d5166 / #2e3d4d / ciel #87b8e6 — marine banni.
//
// LOT A (moteur contrat templatable) — 2 paramètres OPTIONNELS rétrocompatibles :
//   - clauseAnnulation : paragraphe juridique {clause_annulation} PRÉ-RÉSOLU côté
//     serveur (lib/cancellation-policy.js::fetchContratClauseAnnulation). Absent →
//     placeholder d'avertissement admin (jamais un paragraphe deviné).
//   - signature : { signedAt, signatureId } (contract_signed_at +
//     contract_signature_id) pour le rendu POST-signature → la ligne de signature
//     voyageur imprime la date/heure réelle + l'identifiant court au lieu des
//     blancs. Absent/incomplet → blancs (rendu pré-signature).
export function genContratHTML({ log, reservation, cfg, prop, mode = 'preview', cautionDue, clauseAnnulation, signature, loueurConsent }) {
  const emitter = resolveEmitter({ cfg, prop })
  const dateEmission = new Date().toLocaleDateString('fr-FR')

  // LOT D (consentement signature proprio) — param OPTIONNEL rétrocompatible,
  // même doctrine que `cautionDue` : gate UNIQUEMENT sur un `false` EXPLICITE.
  // `loueurConsent` est PRÉ-RÉSOLU par le call-site (lib/proprio-consent.js ::
  // resolveLoueurConsentForContract) : contrat-html reste PUR (aucun accès DB →
  // parité byte-identique lib/ ↔ src/lib). undefined (défaut, call-sites non
  // branchés/legacy, erreur DB, table pas migrée) → rendu normal (fail-open
  // VOULU, décision Dimitri : ne jamais bloquer une résa). false + émetteur
  // PROPRIO (pas isSousLoc — Maison Zen p6 exclue) → le bloc signature loueur
  // affiche « Signature à apposer manuellement » à la place du nom.
  const loueurManualSign = !emitter.isSousLoc && loueurConsent === false

  // Nom affiché (en-tête, carte « Logement loué », titre) : le nom PROPRE du
  // logement (30/09). Plus de repli sur l'identifiant technique (« lc2 ») : un
  // contrat sans nom est REFUSÉ en amont par chaque appelant
  // (RAISON_CONTRAT_SANS_NOM) ; si l'un l'oubliait, la case reste vide plutôt
  // que d'imprimer un code dans un document signé.
  const logementNom = nomLogementFacture(log) || ''

  const voyageurNom = reservation
    ? [reservation.guest_first_name, reservation.guest_last_name].filter(Boolean).join(' ') || (reservation.voyageur || '—')
    : '________________________________'
  const voyageurEmail = reservation?.guest_email || reservation?.email || '________________________________'
  const voyageurTel = reservation?.guest_phone || reservation?.phone || '________________________________'
  const dateArrivee = reservation?.start_date ? new Date(reservation.start_date).toLocaleDateString('fr-FR') : '____ / ____ / ________'
  const dateDepart = reservation?.end_date ? new Date(reservation.end_date).toLocaleDateString('fr-FR') : '____ / ____ / ________'
  const nbVoyageurs = reservation?.guest_count || reservation?.nbVoyageurs || '____'
  const montant = reservation?.total_amount != null ? `${Number(reservation.total_amount).toFixed(2)} €` : '________ €'
  // WAGON R5 (A4 — unification caution) puis lot argent 3d (07/10) : le montant
  // vient de la règle UNIQUE resolveCautionFromLog, la même que la préautorisation
  // Stripe et le verdict cautionDue : champ vide → caution du logement parent
  // (chambre sous-loc), 0 → pas de caution (case vide), > 0 → la valeur. L'ancien
  // repli legacy log.pricing?.deposit ?? log.deposit est retiré (troisième source
  // que ni Stripe ni le site ne lisaient). P-2.56 (30/10) : montant FIGÉ de la
  // résa quand elle le porte, sinon le réglage pour la durée du séjour (paliers)
  // — la même porte que la session Stripe.
  const cautionValue = cautionPourSejour({
    figee: reservation || null,
    log,
    nuits: nuitsEntre(reservation?.start_date, reservation?.end_date),
  }).montant
  const caution = cautionValue != null && cautionValue !== '' && Number.isFinite(Number(cautionValue)) && Number(cautionValue) > 0 ? `${Number(cautionValue).toFixed(2)} €` : '________ €'

  // LOT 2 (A-4 CONTRAT) — surface ANNONCE juridique. La MENTION de la caution
  // (ligne récap CAUTION + articles caution des clauses v2 via les drapeaux
  // caution_due/caution_absente) est MASQUÉE si — et seulement si — la matrice
  // caution dit OFF pour ce (logement × plateforme). `cautionDue` est PRÉ-RÉSOLU
  // par le call-site via la dérivation unique (isCautionDueForStay, resolver
  // source unique) : contrat-html reste PUR. Masquage UNIQUEMENT sur un `false`
  // EXPLICITE : undefined (défaut, aucun intrant / call-site non branché) →
  // mention CONSERVÉE (invariant SACRÉ : matrice vide → CODED_DEFAULTS
  // booking/direct on → cautionDue jamais false → contrat inchangé). Le MONTANT
  // (`caution` ci-dessus) garde sa sémantique historique (§2.3, jamais
  // rebranché) : on gate la PRÉSENCE de la mention, pas la valeur.
  const cautionHidden = cautionDue === false

  // Lot E (Chantier 12) — field-first : checkin_time/checkout_time (Instructions,
  // source de vérité ADR-199) priment sur schedule (résidu), comme les 3 autres
  // familles de résolveurs. Cohérence inter-canaux 17h sur lc1/lc2/lc3. Défaut
  // 16:00/10:00 conservé en dernier recours.
  const checkinTime = log.checkin_time || log.schedule?.checkin_start || '16:00'
  const checkoutTime = log.checkout_time || log.schedule?.checkout_time || '10:00'

  // Q8 ADR-063 : adresse_complete (privée, contrat) prioritaire sur
  // public_address (partielle Airbnb). Si adresse_complete vide, fallback
  // sur public_address.* en concaténation (mieux que vide).
  const publicAddressParts = log.public_address
    ? [log.public_address.quartier, log.public_address.code_postal, log.public_address.ville].filter(Boolean).join(' ')
    : ''
  const adressePublique = log.adresse_complete || publicAddressParts || ''

  // CONTRAT V2 (art. 16) — coordonnées du médiateur de la consommation, servies
  // aux clauses via {mediateur_nom}/{mediateur_coordonnees}. Config SIMPLE :
  // cfg.co.mediateur_nom / cfg.co.mediateur_coordonnees (blob fbz8-cfg,
  // informations société), fallback env MEDIATEUR_NOM / MEDIATEUR_COORDONNEES
  // (côté client CRA les env sont absentes → cfg seul). ABSENTES → '' → le
  // paragraphe médiateur de l'art. 16 NE SE REND PAS ({#if … ET …}) — jamais de
  // médiateur inventé dans un contrat signé (tâche médiateur = Dimitri, E3).
  const mediateurNom = (cfg && cfg.co && cfg.co.mediateur_nom) || process.env.MEDIATEUR_NOM || ''
  const mediateurCoordonnees = (cfg && cfg.co && cfg.co.mediateur_coordonnees) || process.env.MEDIATEUR_COORDONNEES || ''

  const clausesSource = log.contrat_clauses || DEFAULT_CLAUSES
  // LOT A — moteur de template des clauses : {var} + blocs {#if flag}…{/if}
  // rendus sur le TEXTE, AVANT le pipeline HTML (renderClausesHtml). Un token
  // inconnu résiduel est purgé ('' + console.error) — jamais en clair dans un
  // PDF signé (la validation au save refuse déjà l'écriture).
  const contratVars = buildContratVars({
    emitter,
    log,
    reservation,
    dateEmission,
    voyageurNom,
    voyageurEmail,
    voyageurTel,
    dateArrivee,
    dateDepart,
    checkinTime,
    checkoutTime,
    cautionValue,
    adressePublique,
    clauseAnnulation,
    cautionHidden,
    mediateurNom,
    mediateurCoordonnees,
  })
  const clausesText = renderContratClauses(clausesSource, contratVars)
  const clauses = renderClausesHtml(clausesText)

  // LOT A (#4) — ligne de signature voyageur DATÉE en rendu post-signature :
  // date/heure réelle (Europe/Paris) + identifiant court de la preuve
  // (contract_signature_id sans tirets, 8 premiers caractères). Signature
  // absente ou invalide → blancs historiques (rendu pré-signature inchangé).
  const signedAtFr = signature ? formatSignatureDateTimeFr(signature.signedAt) : ''
  const signatureShortId = signature && typeof signature.signatureId === 'string' && signature.signatureId.trim()
    ? signature.signatureId.replace(/-/g, '').slice(0, 8)
    : ''
  const voyageurSigDateLine = signedAtFr && signatureShortId
    ? `Le ${signedAtFr} — signature électronique n° ${signatureShortId}`
    : 'Le ____ / ____ / ________'

  // Bloc LOUEUR (parties) : l'émetteur FBZ SAS garde ses mentions légales
  // (SIRET) ICI — le pied de page, lui, reste au format commun sans mention.
  // NOM DU LOUEUR SUR LE CONTRAT — « Prénom NOM » (Dimitri 2026-08-04).
  // `emitter.nom` porte l'ordre administratif « NOM Prénom », voulu pour le tri
  // des listes et conservé sur la facture. Sur un acte qui engage le loueur, on
  // écrit le nom comme une personne le signe. Repli sur `emitter.nom` si les
  // parties ne sont pas renseignées (proprios non migrés, émetteur société).
  const loueurNomAffiche =
    [emitter.prenom, String(emitter.nom_famille || '').toUpperCase()]
      .filter(Boolean)
      .join(' ') || emitter.nom
  const emitterAdresseLine = [emitter.adresse, [emitter.code_postal, emitter.ville].filter(Boolean).join(' ')].filter(Boolean).join(', ')
  const emitterContactLine = [emitter.email, emitter.telephone].filter(Boolean).map(escapeHtml).join(' · ')
  // ── IDENTITÉ DU LOUEUR INCOMPLÈTE → DOCUMENT MARQUÉ NON CONTRACTUEL ────────
  // MIROIR BOOLÉEN de lib/contrat-loueur-identite.js::verifierIdentiteLoueur
  // (ce module serveur fait FOI). Il est INLINÉ ici et non importé : CRA interdit
  // d'importer `lib/` depuis `src/`, et ce fichier doit rester byte-identique à
  // son miroir ESM (même doctrine que propDisplayName inliné dans
  // lib/emitter-resolver.js). Équivalence stricte : le verdict serveur est faux
  // ssi le nom OU l'adresse manque, OU l'adresse est là sans CP/ville — c'est
  // exactement la disjonction ci-dessous. Sous-location FBZ EXCLUE (coordonnées
  // portées par cfg.co, cf. la garde `isSousLoc` du module).
  //
  // POURQUOI un bandeau et non un repli discret : les chemins VOYAGEUR
  // (contract_read, contract_sign, api/contract-pdf) sont désormais bloqués en
  // amont, mais l'APERÇU ADMIN et l'envoi manuel du contrat par email
  // (src/tabs/logements/ContratTab.jsx, src/modals/ResaV2Modal.jsx) rendent ce
  // HTML côté client SANS passer par ces gardes. L'ancien repli
  // « (adresse à renseigner) » produisait un document qui ressemblait à un
  // contrat valide. Un document qui n'en est pas un doit se voir comme tel.
  const loueurIncomplet =
    !emitter.isSousLoc &&
    (!String(emitter.nom || '').trim() ||
      !String(emitter.adresse || '').trim() ||
      !String(emitter.code_postal || '').trim() ||
      !String(emitter.ville || '').trim())
  const loueurMeta = [
    emitterAdresseLine
      ? escapeHtml(emitterAdresseLine)
      : '<span class="meta-warn">ADRESSE DU BAILLEUR MANQUANTE — DOCUMENT NON CONTRACTUEL</span>',
    emitter.siret ? `SIRET ${escapeHtml(emitter.siret)}` : '',
    emitterContactLine,
  ].filter(Boolean).join('<br>')
  const voyageurContactLine = [voyageurEmail, voyageurTel].map(escapeHtml).join(' · ')
  const voyageurMeta = `${voyageurContactLine}<br>${escapeHtml(String(nbVoyageurs))} voyageur(s)`

  // Carte LOGEMENT : adresse + composition sur une ligne méta.
  const lodgingMeta = [
    adressePublique ? escapeHtml(adressePublique) : '',
    log.capacity ? escapeHtml(`${log.capacity.max_persons || '?'} personne(s) max · ${log.capacity.bedrooms ?? '?'} chambre(s) · ${log.capacity.bathrooms ?? '?'} salle(s) de bain`) : '',
  ].filter(Boolean).join(' · ')

  const html = `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>Contrat ${escapeHtml(logementNom)} — ${dateEmission}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:-apple-system,'Segoe UI',Roboto,Arial,sans-serif;background:#eef1f4;padding:22px 12px;color:#1f2933}
  .page{background:#fff;max-width:760px;margin:0 auto;border-radius:12px;overflow:hidden;box-shadow:0 8px 30px rgba(31,41,51,.10)}
  .head{padding:32px 48px 0;text-align:center}
  .eyebrow{font-size:10px;letter-spacing:1.4px;color:#b4bcc4}
  .head-name{font-size:17px;font-weight:700;color:#233240;margin-top:6px}
  .head-sub{font-size:11px;color:#9aa4b0;margin-top:4px}
  .rule-sky{margin:20px 48px 0;border-top:1px solid #87b8e6}
  .body{padding:22px 48px 30px}
  .parties{display:flex;justify-content:space-between;gap:24px}
  .party-right{text-align:right}
  .plabel{font-size:10px;letter-spacing:1.4px;color:#b4bcc4;font-weight:700}
  .pname{font-size:12.5px;font-weight:700;color:#1f2933;margin-top:4px}
  .pmeta{font-size:11px;color:#9aa4b0;line-height:16px}
  .meta-warn{font-style:italic;font-weight:700;color:#A32D2D}
  .doc-invalide{background:#FCEBEB;border-bottom:3px solid #E24B4A;color:#A32D2D;padding:14px 20px;font-size:11.5px;font-weight:700;line-height:1.5;text-align:center;letter-spacing:.3px}
  .lodging{margin-top:20px;background:#f7fafc;border-radius:8px;padding:16px 18px}
  .lodging-rule{margin:12px 0;border-top:1px solid #e3e9ef}
  .stay-grid,.amounts{display:flex;gap:24px}
  .stay-grid>div,.amounts>div{flex:1}
  .svalue{font-size:12px;font-weight:600;color:#1f2933;margin-top:3px}
  .avalue{font-size:15px;font-weight:700;color:#3d5166;margin-top:3px}
  .cg{margin-top:22px;border-top:1.5px solid #3d5166;padding-top:16px}
  .cg-label{font-size:10px;letter-spacing:1.4px;color:#b4bcc4;font-weight:700;margin-bottom:12px}
  .art-title{font-size:12px;font-weight:700;color:#3d5166;margin:16px 0 6px;page-break-after:avoid}
  .art-sub{font-size:11.5px;font-weight:700;color:#3d5166;margin:12px 0 5px;page-break-after:avoid}
  .clause-p{font-size:11.5px;color:#444;line-height:1.65;margin-bottom:9px}
  .clause-table{border-collapse:collapse;width:100%;max-width:430px;margin:2px 0 10px}
  .clause-table td{padding:5px 0;border-bottom:.5px solid #eef1f4}
  .ct-label{font-size:11px;color:#9aa4b0;padding-right:14px;width:45%}
  .ct-value{font-size:11.5px;color:#1f2933;font-weight:600}
  .clause-list{margin:2px 0 10px 18px}
  .clause-list li{font-size:11.5px;color:#444;line-height:1.65;margin-bottom:3px}
  .sig-section{margin-top:26px;border-top:1.5px solid #3d5166;padding-top:14px;page-break-inside:avoid}
  .made-in{font-size:11px;color:#9aa4b0}
  .signatures{display:flex;gap:40px;margin-top:14px}
  .sig-block{flex:1}
  .role{font-size:10px;letter-spacing:1.4px;color:#b4bcc4;font-weight:700}
  .name{font-size:12px;font-weight:700;color:#3d5166;margin-top:4px}
  .date{font-size:11px;color:#9aa4b0;margin-top:3px}
  .sig-zone{height:44px;border-bottom:1px solid #d5dbe2;margin-top:6px}
  .pre{font-size:9.5px;color:#b4bcc4;margin-top:6px;font-style:italic}
  .footer{background:#f7fafc;padding:12px 20px;text-align:center;font-size:10.5px;color:#9aa4b0}
  .print-btn{position:fixed;top:20px;right:20px;background:#87b8e6;color:#2e3d4d;border:none;border-radius:10px;padding:12px 20px;font-size:13px;font-weight:700;cursor:pointer;box-shadow:0 4px 12px rgba(0,0,0,.15);font-family:inherit;z-index:100}
  @media print{
    body{padding:0;background:#fff}
    .page{box-shadow:none;border-radius:0;max-width:100%}
    .print-btn{display:none!important}
    *{-webkit-print-color-adjust:exact;color-adjust:exact;print-color-adjust:exact}
  }
  @media (max-width:600px){
    .head{padding:26px 20px 0}
    .rule-sky{margin:16px 20px 0}
    .body{padding:18px 20px 24px}
    .parties{flex-direction:column;gap:16px}
    .party-right{text-align:left}
    .stay-grid,.amounts{flex-direction:column;gap:10px}
    .signatures{flex-direction:column;gap:22px}
  }
</style>
</head>
<body>
${mode === 'preview' ? `<button class="print-btn" onclick="window.print()">🖨️ Imprimer / Enregistrer en PDF</button>` : ''}
<div class="page">
${loueurIncomplet ? `  <div class="doc-invalide">DOCUMENT NON CONTRACTUEL — l'identité du bailleur est incomplète (nom, adresse, code postal et ville sont des mentions obligatoires). Ce document ne peut être ni signé ni envoyé au voyageur. À compléter dans Logements &gt; fiche du logement &gt; Info FBZ &gt; Administration.</div>
` : ''}  <div class="head">
    <div class="eyebrow">CONTRAT DE LOCATION</div>
    <div class="head-name">${escapeHtml(logementNom)}</div>
    <div class="head-sub">Location meublée de courte durée à usage exclusif de villégiature</div>
  </div>
  <div class="rule-sky"></div>

  <div class="body">
    <div class="parties">
      <div class="party">
        <div class="plabel">LE LOUEUR</div>
        <div class="pname">${escapeHtml(loueurNomAffiche) || '________________________________'}</div>
        <div class="pmeta">${loueurMeta}</div>
      </div>
      <div class="party party-right">
        <div class="plabel">LE VOYAGEUR</div>
        <div class="pname">${escapeHtml(voyageurNom)}</div>
        <div class="pmeta">${voyageurMeta}</div>
      </div>
    </div>

    <div class="lodging">
      <div class="plabel">LE LOGEMENT</div>
      <div class="pname">${escapeHtml(logementNom)}</div>
      ${lodgingMeta ? `<div class="pmeta">${lodgingMeta}</div>` : ''}
      <div class="lodging-rule"></div>
      <div class="stay-grid">
        <div><div class="plabel">ARRIVÉE</div><div class="svalue">${dateArrivee} à ${escapeHtml(checkinTime)}</div></div>
        <div><div class="plabel">DÉPART</div><div class="svalue">${dateDepart} à ${escapeHtml(checkoutTime)}</div></div>
      </div>
      <div class="lodging-rule"></div>
      <div class="amounts">
        <div><div class="plabel">MONTANT DU SÉJOUR</div><div class="avalue">${montant}</div></div>
        ${cautionHidden ? '' : `<div><div class="plabel">CAUTION</div><div class="avalue">${caution}</div></div>`}
      </div>
    </div>

    <div class="cg">
      <div class="cg-label">CONDITIONS GÉNÉRALES</div>
      <div class="clauses">${clauses}</div>
    </div>

    <div class="sig-section">
      <div class="made-in">Fait en deux exemplaires, dont un remis à chaque partie.</div>
      <div class="signatures">
        <div class="sig-block">
          <div class="role">LE LOUEUR</div>
          <div class="name">${loueurManualSign ? 'Signature à apposer manuellement' : escapeHtml(loueurNomAffiche)}</div>
          <div class="date">Le ${dateEmission}</div>
          <div class="sig-zone"></div>
          <div class="pre">Signature électronique le ${escapeHtml(dateEmission)}</div>
        </div>
        <div class="sig-block">
          <div class="role">LE VOYAGEUR</div>
          <div class="name">${escapeHtml(voyageurNom)}</div>
          <div class="date">${escapeHtml(voyageurSigDateLine)}</div>
          <div class="sig-zone"></div>
          <div class="pre">${signedAtFr
            ? 'Signature électronique le ' + escapeHtml(signedAtFr)
            : 'Signature électronique le ____ / ____ / ________'}</div>
        </div>
      </div>
    </div>
  </div>

  <div class="footer">Contrat émis le ${dateEmission} par ${escapeHtml(loueurNomAffiche)}</div>
</div>
</body>
</html>`

  return { html, dateEmission, voyageurEmail }
}

function escapeHtml(s) {
  if (s == null) return ''
  return String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}
